/**
 * ProceduralItemIcons — generates 34×34 canvas-based placeholder icons
 * for items that lack a loaded sprite icon.
 *
 * Priority chain (handled externally):
 *   1. Custom folder:  public/sprites/items/icons/{baseId}.png
 *   2. Mapped 7soul/db32 icons (ItemIcons.ts)
 *   3. This module (procedural fallback)
 */
import { EquipmentSlot, ItemRarity, WeaponSubType, DefenseType } from '@shared/types';
import { ITEM, ELEMENTS } from '@shared/constants';

const ICON_SIZE = 34;

/** Internal cache: key → canvas */
const cache = new Map<string, HTMLCanvasElement>();

/** Element accent colors for prefix indicators */
const ELEMENT_ACCENT: Record<string, string> = {
  od: ELEMENTS.od.color,
  su: ELEMENTS.su.color,
  gok: ELEMENTS.gok.color,
  karanlik: ELEMENTS.karanlik.color,
  physical: ELEMENTS.physical.color,
};

/** Get (or generate) a procedural placeholder icon for the given slot & rarity. */
export function getProceduralIcon(slot: EquipmentSlot, rarity: ItemRarity): HTMLCanvasElement {
  const key = `${slot}_${rarity}`;
  let canvas = cache.get(key);
  if (canvas) return canvas;

  canvas = document.createElement('canvas');
  canvas.width = ICON_SIZE;
  canvas.height = ICON_SIZE;
  const ctx = canvas.getContext('2d')!;

  const color = ITEM.COLORS[rarity] ?? '#a0a0a0';

  // --- Background ---
  ctx.fillStyle = '#1a1a2e';
  ctx.beginPath();
  ctx.roundRect(1, 1, ICON_SIZE - 2, ICON_SIZE - 2, 4);
  ctx.fill();

  // --- Rarity border ---
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.roundRect(1, 1, ICON_SIZE - 2, ICON_SIZE - 2, 4);
  ctx.stroke();

  // --- Slot-specific silhouette ---
  drawSlotShape(ctx, slot, color);

  cache.set(key, canvas);
  return canvas;
}

/**
 * Get a detailed procedural icon with weapon sub-type, defense type, and element accent.
 * Used for inventory rendering when more detail is available.
 */
export function getDetailedProceduralIcon(
  slot: EquipmentSlot,
  rarity: ItemRarity,
  weaponSubType?: WeaponSubType,
  defenseType?: DefenseType,
  elementHint?: string,
): HTMLCanvasElement {
  const key = `${slot}_${rarity}_${weaponSubType || 'none'}_${defenseType || 'none'}_${elementHint || 'none'}`;
  let canvas = cache.get(key);
  if (canvas) return canvas;

  canvas = document.createElement('canvas');
  canvas.width = ICON_SIZE;
  canvas.height = ICON_SIZE;
  const ctx = canvas.getContext('2d')!;

  const color = ITEM.COLORS[rarity] ?? '#a0a0a0';

  // --- Background with subtle defense type tint ---
  const bgColor = defenseType ? getDefenseBgTint(defenseType) : '#1a1a2e';
  ctx.fillStyle = bgColor;
  ctx.beginPath();
  ctx.roundRect(1, 1, ICON_SIZE - 2, ICON_SIZE - 2, 4);
  ctx.fill();

  // --- Rarity border ---
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.roundRect(1, 1, ICON_SIZE - 2, ICON_SIZE - 2, 4);
  ctx.stroke();

  // --- Draw weapon sub-type or slot shape ---
  if (slot === 'weapon' && weaponSubType) {
    drawWeaponByType(ctx, ICON_SIZE / 2, color, weaponSubType);
  } else if (slot === 'offhand' && weaponSubType) {
    drawWeaponByType(ctx, ICON_SIZE / 2, color, weaponSubType);
  } else {
    drawSlotShape(ctx, slot, color);
  }

  // --- Element accent (small colored dot in corner for elemental items) ---
  if (elementHint && elementHint !== 'physical' && ELEMENT_ACCENT[elementHint]) {
    const accent = ELEMENT_ACCENT[elementHint];
    ctx.fillStyle = accent;
    ctx.beginPath();
    ctx.arc(ICON_SIZE - 6, 6, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 0.5;
    ctx.stroke();
  }

  // --- Defense type indicator (small icon in bottom-left) ---
  if (defenseType && slot !== 'weapon') {
    drawDefenseIndicator(ctx, defenseType);
  }

  cache.set(key, canvas);
  return canvas;
}

/** Background tint based on defense type */
function getDefenseBgTint(dt: DefenseType): string {
  switch (dt) {
    case 'armour': return '#1e1a16';          // Warm brown
    case 'evasion': return '#161e1a';         // Cool green
    case 'energyShield': return '#1a1628';    // Purple
    case 'armour_evasion': return '#1a1c16';  // Brown-green
    case 'armour_energyShield': return '#1c1620'; // Brown-purple
    case 'evasion_energyShield': return '#161c22'; // Green-purple
    default: return '#1a1a2e';
  }
}

/** Small defense type indicator in bottom-left corner */
function drawDefenseIndicator(ctx: CanvasRenderingContext2D, dt: DefenseType): void {
  const x = 4, y = ICON_SIZE - 8;
  ctx.font = 'bold 7px Arial';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';

  switch (dt) {
    case 'armour':
      ctx.fillStyle = '#8b7355';
      ctx.fillText('AR', x, y);
      break;
    case 'evasion':
      ctx.fillStyle = '#55886b';
      ctx.fillText('EV', x, y);
      break;
    case 'energyShield':
      ctx.fillStyle = '#7755aa';
      ctx.fillText('ES', x, y);
      break;
    case 'armour_evasion':
      ctx.fillStyle = '#8b7355';
      ctx.fillText('A', x, y);
      ctx.fillStyle = '#55886b';
      ctx.fillText('E', x + 5, y);
      break;
    case 'armour_energyShield':
      ctx.fillStyle = '#8b7355';
      ctx.fillText('A', x, y);
      ctx.fillStyle = '#7755aa';
      ctx.fillText('S', x + 5, y);
      break;
    case 'evasion_energyShield':
      ctx.fillStyle = '#55886b';
      ctx.fillText('E', x, y);
      ctx.fillStyle = '#7755aa';
      ctx.fillText('S', x + 5, y);
      break;
  }
}

/** Draw weapon silhouette based on sub-type */
function drawWeaponByType(ctx: CanvasRenderingContext2D, cx: number, color: string, wst: WeaponSubType): void {
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  switch (wst) {
    case 'sword': drawSword(ctx, cx, color); break;
    case 'great_sword': drawGreatSword(ctx, cx, color); break;
    case 'axe': drawAxe(ctx, cx, color); break;
    case 'great_axe': drawGreatAxe(ctx, cx, color); break;
    case 'hammer': drawHammer(ctx, cx, color); break;
    case 'dagger': drawDagger(ctx, cx, color); break;
    case 'bow': drawBow(ctx, cx, color); break;
    case 'staff': drawStaff(ctx, cx, color); break;
    case 'wand': drawWand(ctx, cx, color); break;
    default: drawSword(ctx, cx, color); break;
  }
}

// ─────────────────────── shape drawing ───────────────────────

function drawSlotShape(
  ctx: CanvasRenderingContext2D,
  slot: EquipmentSlot,
  color: string,
): void {
  const cx = ICON_SIZE / 2;         // 17
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  switch (slot) {
    case 'weapon':   drawSword(ctx, cx, color);   break;
    case 'head':     drawHelmet(ctx, cx, color);   break;
    case 'chest':    drawArmor(ctx, cx, color);    break;
    case 'gloves':   drawGloves(ctx, cx, color);   break;
    case 'boots':    drawBoots(ctx, cx, color);    break;
    case 'belt':     drawBelt(ctx, cx, color);     break;
    case 'offhand':  drawShield(ctx, cx, color);   break;
    case 'amulet':   drawAmulet(ctx, cx, color);   break;
    case 'ring1':
    case 'ring2':    drawRing(ctx, cx, color);     break;
    case 'cape':     drawCape(ctx, cx, color);     break;
    default:         drawDiamond(ctx, cx, color);  break;
  }
}

/* ── Sword ── */
function drawSword(ctx: CanvasRenderingContext2D, cx: number, color: string) {
  ctx.fillStyle = color;
  // Blade
  ctx.beginPath();
  ctx.moveTo(cx, 5);
  ctx.lineTo(cx + 4, 16);
  ctx.lineTo(cx, 19);
  ctx.lineTo(cx - 4, 16);
  ctx.closePath();
  ctx.fill();
  // Guard
  ctx.beginPath();
  ctx.moveTo(cx - 7, 19);
  ctx.lineTo(cx + 7, 19);
  ctx.stroke();
  // Handle
  ctx.beginPath();
  ctx.moveTo(cx, 20);
  ctx.lineTo(cx, 27);
  ctx.stroke();
  // Pommel
  ctx.beginPath();
  ctx.arc(cx, 28, 1.8, 0, Math.PI * 2);
  ctx.fill();
}

/* ── Helmet ── */
function drawHelmet(ctx: CanvasRenderingContext2D, cx: number, _color: string) {
  // Dome
  ctx.beginPath();
  ctx.arc(cx, 14, 9, Math.PI, 0);
  ctx.lineTo(cx + 9, 22);
  ctx.lineTo(cx + 7, 25);
  ctx.lineTo(cx - 7, 25);
  ctx.lineTo(cx - 9, 22);
  ctx.closePath();
  ctx.fill();
  // Visor slit
  ctx.strokeStyle = '#1a1a2e';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(cx - 5, 18);
  ctx.lineTo(cx + 5, 18);
  ctx.stroke();
}

/* ── Armor ── */
function drawArmor(ctx: CanvasRenderingContext2D, cx: number, _color: string) {
  ctx.beginPath();
  // Shoulders
  ctx.moveTo(cx - 10, 8);
  ctx.lineTo(cx - 5, 6);
  ctx.lineTo(cx, 8);
  ctx.lineTo(cx + 5, 6);
  ctx.lineTo(cx + 10, 8);
  // Right side
  ctx.lineTo(cx + 11, 14);
  ctx.lineTo(cx + 8, 27);
  // Bottom
  ctx.lineTo(cx, 29);
  // Left side
  ctx.lineTo(cx - 8, 27);
  ctx.lineTo(cx - 11, 14);
  ctx.closePath();
  ctx.fill();
  // Center line
  ctx.strokeStyle = '#1a1a2e';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx, 9);
  ctx.lineTo(cx, 28);
  ctx.stroke();
}

/* ── Gloves ── */
function drawGloves(ctx: CanvasRenderingContext2D, cx: number, _color: string) {
  // Palm
  ctx.beginPath();
  ctx.roundRect(cx - 6, 12, 12, 14, 2);
  ctx.fill();
  // Fingers
  ctx.fillRect(cx - 5, 6, 3, 7);
  ctx.fillRect(cx - 1, 4, 3, 9);
  ctx.fillRect(cx + 3, 6, 3, 7);
  // Cuff
  ctx.fillRect(cx - 7, 25, 14, 4);
}

/* ── Boots ── */
function drawBoots(ctx: CanvasRenderingContext2D, cx: number, _color: string) {
  ctx.beginPath();
  // Shaft
  ctx.moveTo(cx - 5, 6);
  ctx.lineTo(cx + 4, 6);
  ctx.lineTo(cx + 4, 20);
  // Toe
  ctx.lineTo(cx + 10, 20);
  ctx.lineTo(cx + 10, 24);
  // Sole
  ctx.lineTo(cx + 11, 26);
  ctx.lineTo(cx + 11, 28);
  ctx.lineTo(cx - 6, 28);
  ctx.lineTo(cx - 6, 24);
  ctx.lineTo(cx - 5, 20);
  ctx.closePath();
  ctx.fill();
}

/* ── Belt ── */
function drawBelt(ctx: CanvasRenderingContext2D, cx: number, color: string) {
  // Belt strap
  ctx.fillStyle = color;
  ctx.fillRect(4, 13, ICON_SIZE - 8, 8);
  // Buckle (darker inset)
  ctx.strokeStyle = '#1a1a2e';
  ctx.lineWidth = 1.5;
  ctx.strokeRect(cx - 4, 14, 8, 6);
  // Buckle pin
  ctx.fillStyle = '#1a1a2e';
  ctx.fillRect(cx - 1, 15, 2, 4);
}

/* ── Shield ── */
function drawShield(ctx: CanvasRenderingContext2D, cx: number, _color: string) {
  ctx.beginPath();
  ctx.moveTo(cx, 5);
  ctx.lineTo(cx + 10, 9);
  ctx.lineTo(cx + 10, 18);
  ctx.lineTo(cx, 28);
  ctx.lineTo(cx - 10, 18);
  ctx.lineTo(cx - 10, 9);
  ctx.closePath();
  ctx.fill();
  // Cross emblem
  ctx.strokeStyle = '#1a1a2e';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(cx, 10);
  ctx.lineTo(cx, 22);
  ctx.moveTo(cx - 5, 15);
  ctx.lineTo(cx + 5, 15);
  ctx.stroke();
}

/* ── Amulet ── */
function drawAmulet(ctx: CanvasRenderingContext2D, cx: number, color: string) {
  // Chain
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(cx, 6, 6, 0.15 * Math.PI, 0.85 * Math.PI);
  ctx.stroke();
  // Pendant gem
  ctx.fillStyle = color;
  ctx.beginPath();
  // Diamond-shaped gem
  ctx.moveTo(cx, 14);
  ctx.lineTo(cx + 6, 21);
  ctx.lineTo(cx, 28);
  ctx.lineTo(cx - 6, 21);
  ctx.closePath();
  ctx.fill();
  // Gem highlight
  ctx.fillStyle = 'rgba(255,255,255,0.3)';
  ctx.beginPath();
  ctx.moveTo(cx, 15);
  ctx.lineTo(cx + 3, 20);
  ctx.lineTo(cx, 22);
  ctx.closePath();
  ctx.fill();
}

/* ── Ring ── */
function drawRing(ctx: CanvasRenderingContext2D, cx: number, color: string) {
  // Band
  ctx.strokeStyle = color;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(cx, 19, 8, 0, Math.PI * 2);
  ctx.stroke();
  // Gem setting
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(cx, 11, 4, 0, Math.PI * 2);
  ctx.fill();
  // Gem highlight
  ctx.fillStyle = 'rgba(255,255,255,0.35)';
  ctx.beginPath();
  ctx.arc(cx - 1, 10, 1.5, 0, Math.PI * 2);
  ctx.fill();
}

/* ── Cape ── */
function drawCape(ctx: CanvasRenderingContext2D, cx: number, _color: string) {
  ctx.beginPath();
  // Collar
  ctx.moveTo(cx - 8, 6);
  ctx.lineTo(cx + 8, 6);
  // Right side with wave
  ctx.quadraticCurveTo(cx + 10, 16, cx + 7, 28);
  // Bottom wave
  ctx.quadraticCurveTo(cx + 3, 25, cx, 28);
  ctx.quadraticCurveTo(cx - 3, 25, cx - 7, 28);
  // Left side
  ctx.quadraticCurveTo(cx - 10, 16, cx - 8, 6);
  ctx.closePath();
  ctx.fill();
  // Collar detail
  ctx.strokeStyle = '#1a1a2e';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - 6, 8);
  ctx.lineTo(cx + 6, 8);
  ctx.stroke();
}

/* ── Great Sword (two-handed, larger blade) ── */
function drawGreatSword(ctx: CanvasRenderingContext2D, cx: number, color: string) {
  ctx.fillStyle = color;
  // Long blade
  ctx.beginPath();
  ctx.moveTo(cx, 3);
  ctx.lineTo(cx + 5, 18);
  ctx.lineTo(cx, 21);
  ctx.lineTo(cx - 5, 18);
  ctx.closePath();
  ctx.fill();
  // Wide guard
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(cx - 9, 21);
  ctx.lineTo(cx + 9, 21);
  ctx.stroke();
  // Long handle
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.moveTo(cx, 22);
  ctx.lineTo(cx, 30);
  ctx.stroke();
  // Pommel
  ctx.beginPath();
  ctx.arc(cx, 31, 2, 0, Math.PI * 2);
  ctx.fill();
}

/* ── Axe (one-handed) ── */
function drawAxe(ctx: CanvasRenderingContext2D, cx: number, color: string) {
  ctx.fillStyle = color;
  // Handle
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(cx, 6);
  ctx.lineTo(cx, 28);
  ctx.stroke();
  // Axe head
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(cx, 8);
  ctx.lineTo(cx + 9, 6);
  ctx.lineTo(cx + 10, 14);
  ctx.lineTo(cx, 16);
  ctx.closePath();
  ctx.fill();
}

/* ── Great Axe (two-handed, larger) ── */
function drawGreatAxe(ctx: CanvasRenderingContext2D, cx: number, color: string) {
  ctx.fillStyle = color;
  // Long handle
  ctx.strokeStyle = color;
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.moveTo(cx, 4);
  ctx.lineTo(cx, 30);
  ctx.stroke();
  // Large double axe head
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(cx, 6);
  ctx.lineTo(cx + 11, 4);
  ctx.lineTo(cx + 12, 14);
  ctx.lineTo(cx, 16);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(cx, 6);
  ctx.lineTo(cx - 11, 4);
  ctx.lineTo(cx - 12, 14);
  ctx.lineTo(cx, 16);
  ctx.closePath();
  ctx.fill();
}

/* ── Hammer (two-handed, heavy head) ── */
function drawHammer(ctx: CanvasRenderingContext2D, cx: number, color: string) {
  ctx.fillStyle = color;
  // Long handle
  ctx.strokeStyle = color;
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.moveTo(cx, 14);
  ctx.lineTo(cx, 30);
  ctx.stroke();
  // Heavy rectangular head
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.roundRect(cx - 10, 4, 20, 12, 2);
  ctx.fill();
  // Head detail line
  ctx.strokeStyle = '#1a1a2e';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx, 5);
  ctx.lineTo(cx, 15);
  ctx.stroke();
}

/* ── Dagger (small, fast) ── */
function drawDagger(ctx: CanvasRenderingContext2D, cx: number, color: string) {
  ctx.fillStyle = color;
  // Short blade
  ctx.beginPath();
  ctx.moveTo(cx, 9);
  ctx.lineTo(cx + 3, 18);
  ctx.lineTo(cx, 20);
  ctx.lineTo(cx - 3, 18);
  ctx.closePath();
  ctx.fill();
  // Small guard
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(cx - 5, 20);
  ctx.lineTo(cx + 5, 20);
  ctx.stroke();
  // Handle
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(cx, 21);
  ctx.lineTo(cx, 26);
  ctx.stroke();
}

/* ── Bow ── */
function drawBow(ctx: CanvasRenderingContext2D, cx: number, color: string) {
  ctx.strokeStyle = color;
  // Bow arc
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(cx + 4, ICON_SIZE / 2, 12, 0.7 * Math.PI, 1.3 * Math.PI);
  ctx.stroke();
  // String
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx + 4 + 12 * Math.cos(0.7 * Math.PI), ICON_SIZE / 2 + 12 * Math.sin(0.7 * Math.PI));
  ctx.lineTo(cx + 4 + 12 * Math.cos(1.3 * Math.PI), ICON_SIZE / 2 + 12 * Math.sin(1.3 * Math.PI));
  ctx.stroke();
  // Arrow
  ctx.fillStyle = color;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - 4, ICON_SIZE / 2);
  ctx.lineTo(cx + 8, ICON_SIZE / 2);
  ctx.stroke();
  // Arrowhead
  ctx.beginPath();
  ctx.moveTo(cx + 10, ICON_SIZE / 2);
  ctx.lineTo(cx + 6, ICON_SIZE / 2 - 3);
  ctx.lineTo(cx + 6, ICON_SIZE / 2 + 3);
  ctx.closePath();
  ctx.fill();
}

/* ── Staff (two-handed, spell focus) ── */
function drawStaff(ctx: CanvasRenderingContext2D, cx: number, color: string) {
  ctx.strokeStyle = color;
  // Long shaft
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.moveTo(cx, 10);
  ctx.lineTo(cx, 30);
  ctx.stroke();
  // Crystal/orb on top
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(cx, 8, 4, 0, Math.PI * 2);
  ctx.fill();
  // Crystal glow
  ctx.fillStyle = 'rgba(255,255,255,0.3)';
  ctx.beginPath();
  ctx.arc(cx - 1, 7, 1.5, 0, Math.PI * 2);
  ctx.fill();
}

/* ── Wand (one-handed, small spell focus) ── */
function drawWand(ctx: CanvasRenderingContext2D, cx: number, color: string) {
  ctx.strokeStyle = color;
  // Short shaft
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(cx, 12);
  ctx.lineTo(cx, 28);
  ctx.stroke();
  // Small gem on top
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(cx, 7);
  ctx.lineTo(cx + 3, 11);
  ctx.lineTo(cx, 13);
  ctx.lineTo(cx - 3, 11);
  ctx.closePath();
  ctx.fill();
  // Sparkle
  ctx.fillStyle = 'rgba(255,255,255,0.4)';
  ctx.beginPath();
  ctx.arc(cx, 10, 1, 0, Math.PI * 2);
  ctx.fill();
}

/* ── Generic diamond ── */
function drawDiamond(ctx: CanvasRenderingContext2D, cx: number, _color: string) {
  const cy = ICON_SIZE / 2;
  ctx.beginPath();
  ctx.moveTo(cx, 6);
  ctx.lineTo(cx + 8, cy);
  ctx.lineTo(cx, ICON_SIZE - 6);
  ctx.lineTo(cx - 8, cy);
  ctx.closePath();
  ctx.fill();
}
